import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  fetchReferential,
  fetchSkillsByGroup,
  fetchGroupsByPosition,
  searchSkillPositions,
  quickCreateSkill,
  quickCreateGroup,
  quickCreatePosition,
  deleteReferentialItem,
  toggleReferentialActive,
  updateReferentialItem,
  triggerTranslation,
  updateTranslation,
  toggleTranslationLock,
  TRANSLATION_LANGS,
  type RefItem,
  type RefTable,
} from '../lib/referentialService';
import {
  parseDocxFile,
  parseExcelFile,
  executeImport,
  type ImportPreview,
} from '../lib/referentialImportService';
import { logger } from '../lib/logger';

// ─── Types ───────────────────────────────────────────────────────────────

interface PositionDetail {
  groups: RefItem[];
  skillsByGroup: Record<string, RefItem[]>;
}

// ─── Insert Point Component ──────────────────────────────────────────────

interface InsertPointProps {
  sectorId: string;
  subsectorId?: string;
  afterId: string;
  addingPositionInSector: string | null;
  addingPositionAfter: string | null;
  newPositionName: string;
  setNewPositionName: (v: string) => void;
  onInsert: () => void;
  onCancel: () => void;
  onActivate: () => void;
  indent: string;
}

const InsertPoint: React.FC<InsertPointProps> = ({
  sectorId,
  afterId,
  addingPositionInSector,
  addingPositionAfter,
  newPositionName,
  setNewPositionName,
  onInsert,
  onCancel,
  onActivate,
  indent,
}) => {
  const isActive = addingPositionInSector === sectorId && addingPositionAfter === afterId;

  if (isActive) {
    return (
      <div className={`px-4 ${indent} py-1`}>
        <input
          type="text"
          value={newPositionName}
          onChange={(e) => setNewPositionName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') onInsert();
            if (e.key === 'Escape') onCancel();
          }}
          onBlur={() => { if (!newPositionName.trim()) onCancel(); }}
          placeholder="Nom du poste + Entrée"
          autoFocus
          className="w-full px-2 py-1.5 text-sm border border-[#00244F]/30 rounded-md focus:outline-none focus:ring-2 focus:ring-[#00244F]/20 bg-white"
        />
      </div>
    );
  }

  return (
    <div
      className={`px-4 ${indent} group`}
      onClick={onActivate}
    >
      <div className="h-0 relative flex items-center justify-center cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity">
        <div className="absolute inset-x-0 top-1/2 border-t border-dashed border-[#00244F]/20" />
        <span className="relative bg-gray-50 px-1.5 text-[10px] text-[#00244F]/40 font-medium">+</span>
      </div>
    </div>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────

const Referentials: React.FC = () => {
  const [sectors, setSectors] = useState<RefItem[]>([]);
  const [subsectors, setSubsectors] = useState<RefItem[]>([]);
  const [positions, setPositions] = useState<RefItem[]>([]);
  const [loading, setLoading] = useState(true);

  const [expandedSectors, setExpandedSectors] = useState<Set<string>>(new Set());
  const [expandedSubsectors, setExpandedSubsectors] = useState<Set<string>>(new Set());
  const [selectedPositionId, setSelectedPositionId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  // Recherche globale : postes contenant une compétence qui matche la requête
  const [skillMatchPositionIds, setSkillMatchPositionIds] = useState<Set<string> | null>(null);

  const [positionDetail, setPositionDetail] = useState<PositionDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Inline add states
  const [addingPositionInSector, setAddingPositionInSector] = useState<string | null>(null);
  const [addingPositionAfter, setAddingPositionAfter] = useState<string | null>(null); // position id to insert after, or 'top' for beginning
  const [newPositionName, setNewPositionName] = useState('');
  const [addingGroup, setAddingGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [addingSkillInGroup, setAddingSkillInGroup] = useState<string | null>(null);
  const [newSkillName, setNewSkillName] = useState('');

  // Import modal
  const [showImportModal, setShowImportModal] = useState(false);
  const [importPreview, setImportPreview] = useState<ImportPreview | null>(null);
  const [importSectorId, setImportSectorId] = useState('');
  const [importSubsectorId, setImportSubsectorId] = useState('');
  const [importing, setImporting] = useState(false);
  const [importLog, setImportLog] = useState<string[]>([]);

  // Edit modal
  const [editItem, setEditItem] = useState<{ item: RefItem; type: 'position' | 'group' | 'skill' } | null>(null);
  const [editNameFr, setEditNameFr] = useState('');

  // Translation
  const [translating, setTranslating] = useState(false);
  const [translationItem, setTranslationItem] = useState<{ item: RefItem; table: RefTable } | null>(null);
  const [translationEdits, setTranslationEdits] = useState<Record<string, string>>({});
  const [translationSaving, setTranslationSaving] = useState(false);

  const groupInputRef = useRef<HTMLInputElement>(null);
  const skillInputRef = useRef<HTMLInputElement>(null);

  // ─── Load initial data ─────────────────────────────────────────────────

  useEffect(() => {
    (async () => {
      try {
        const [s, ss, p] = await Promise.all([
          fetchReferential('sectors'),
          fetchReferential('subsectors'),
          fetchReferential('positions'),
        ]);
        setSectors(s);
        setSubsectors(ss);
        setPositions(p);
        if (s.length > 0) {
          setExpandedSectors(new Set([s[0].id]));
          const firstSectorSubs = ss.filter((sub) => sub.sectorId === s[0].id).map((sub) => sub.id);
          setExpandedSubsectors(new Set(firstSectorSubs));
        }
      } catch (err) {
        logger.error('load_referentials', 'Referentials', { error: String(err) });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // ─── Recherche de compétences (débouncée, DB) ──────────────────────────

  useEffect(() => {
    const q = searchQuery.trim();
    if (q.length < 3) {
      setSkillMatchPositionIds(null);
      return;
    }
    const timer = setTimeout(() => {
      searchSkillPositions(q)
        .then(setSkillMatchPositionIds)
        .catch(() => setSkillMatchPositionIds(null));
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // ─── Auto-expand on search ──────────────────────────────────────────────

  useEffect(() => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return;

    const matchingPositions = positions.filter(
      (p) => p.nameFr.toLowerCase().includes(q) || skillMatchPositionIds?.has(p.id)
    );
    const sectorIds = new Set(matchingPositions.map((p) => p.sectorId).filter(Boolean) as string[]);
    const subsectorIds = new Set(matchingPositions.map((p) => p.subsectorId).filter(Boolean) as string[]);

    setExpandedSectors((prev) => {
      const next = new Set(prev);
      sectorIds.forEach((id) => next.add(id));
      return next;
    });
    setExpandedSubsectors((prev) => {
      const next = new Set(prev);
      subsectorIds.forEach((id) => next.add(id));
      return next;
    });
  }, [searchQuery, positions, skillMatchPositionIds]);

  // ─── Load position detail ──────────────────────────────────────────────

  useEffect(() => {
    if (!selectedPositionId) {
      setPositionDetail(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setDetailLoading(true);
      try {
        const positionGroups = await fetchGroupsByPosition(selectedPositionId);

        const skillsByGroup: Record<string, RefItem[]> = {};
        await Promise.all(
          positionGroups.map(async (g) => {
            const skills = await fetchSkillsByGroup(g.id);
            if (!cancelled) skillsByGroup[g.id] = skills;
          })
        );

        if (!cancelled) {
          setPositionDetail({ groups: positionGroups, skillsByGroup });
          setExpandedGroups(new Set(positionGroups.map((g) => g.id)));
        }
      } catch (err) {
        logger.error('load_position_detail', 'Referentials', { error: String(err) });
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedPositionId]);

  // ─── Derived data ──────────────────────────────────────────────────────

  const selectedPosition = useMemo(
    () => positions.find((p) => p.id === selectedPositionId) || null,
    [positions, selectedPositionId]
  );

  const getSubsectorsForSector = useMemo(() => {
    return (sectorId: string) => subsectors.filter((ss) => ss.sectorId === sectorId);
  }, [subsectors]);

  /** Sous-secteurs d'un secteur groupés par catégorie (pôle), ordre d'apparition. */
  const getGroupedSubsectors = useMemo(() => {
    return (sectorId: string) => {
      const subs = subsectors
        .filter((ss) => ss.sectorId === sectorId)
        .sort((a, b) => a.sortOrder - b.sortOrder);
      const groups: { category: string | null; subs: RefItem[] }[] = [];
      for (const ss of subs) {
        const cat = ss.category || null;
        const last = groups[groups.length - 1];
        if (last && last.category === cat) last.subs.push(ss);
        else groups.push({ category: cat, subs: [ss] });
      }
      return groups;
    };
  }, [subsectors]);

  // Un poste matche si son nom correspond OU s'il contient une compétence qui matche
  const positionMatchesQuery = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    return (p: RefItem) =>
      !q || p.nameFr.toLowerCase().includes(q) || (skillMatchPositionIds?.has(p.id) ?? false);
  }, [searchQuery, skillMatchPositionIds]);

  const filteredPositionsBySector = useMemo(() => {
    return (sectorId: string) =>
      positions.filter((p) => p.sectorId === sectorId && positionMatchesQuery(p));
  }, [positions, positionMatchesQuery]);

  const filteredPositionsBySubsector = useMemo(() => {
    return (subsectorId: string) =>
      positions
        .filter((p) => p.subsectorId === subsectorId && positionMatchesQuery(p))
        .sort((a, b) => a.sortOrder - b.sortOrder);
  }, [positions, positionMatchesQuery]);

  const unclassifiedPositions = useMemo(() => {
    return (sectorId: string) =>
      positions
        .filter((p) => p.sectorId === sectorId && !p.subsectorId && positionMatchesQuery(p))
        .sort((a, b) => a.sortOrder - b.sortOrder);
  }, [positions, positionMatchesQuery]);

  const getSectorName = useCallback(
    (id: string) => sectors.find((s) => s.id === id)?.nameFr || '',
    [sectors]
  );

  // ─── Handlers ──────────────────────────────────────────────────────────

  const handleAddPosition = async (sectorId: string, subsectorId?: string) => {
    const name = newPositionName.trim();
    if (!name) return;
    try {
      const newItem = await quickCreatePosition(name, sectorId, subsectorId);

      if (addingPositionAfter) {
        // Insert after a specific position by adjusting sort_order
        const relevantPositions = positions.filter((p) =>
          p.sectorId === sectorId && (subsectorId ? p.subsectorId === subsectorId : !p.subsectorId)
        ).sort((a, b) => a.sortOrder - b.sortOrder);

        const afterIdx = addingPositionAfter === 'top' ? -1 : relevantPositions.findIndex((p) => p.id === addingPositionAfter);
        const insertSortOrder = afterIdx === -1
          ? (relevantPositions[0]?.sortOrder ?? 0) - 1
          : afterIdx < relevantPositions.length - 1
            ? (relevantPositions[afterIdx].sortOrder + relevantPositions[afterIdx + 1].sortOrder) / 2
            : relevantPositions[afterIdx].sortOrder + 1;

        newItem.sortOrder = insertSortOrder;
        await updateReferentialItem('positions', newItem.id, { sortOrder: insertSortOrder });
      }

      setPositions((prev) => [...prev, newItem].sort((a, b) => a.sortOrder - b.sortOrder));
      setNewPositionName('');
      setAddingPositionInSector(null);
      setAddingPositionAfter(null);
    } catch (err) {
      alert(`Erreur: ${err}`);
    }
  };

  const handleAddGroup = async () => {
    if (!selectedPositionId || !selectedPosition) return;
    const name = newGroupName.trim();
    if (!name) return;
    try {
      const newItem = await quickCreateGroup(name, selectedPositionId, selectedPosition.code);
      setPositionDetail((prev) => {
        if (!prev) return prev;
        return { ...prev, groups: [...prev.groups, newItem], skillsByGroup: { ...prev.skillsByGroup, [newItem.id]: [] } };
      });
      setExpandedGroups((prev) => new Set([...prev, newItem.id]));
      setNewGroupName('');
      setAddingGroup(false);
    } catch (err) {
      alert(`Erreur: ${err}`);
    }
  };

  const handleAddSkill = async (groupId: string) => {
    if (!selectedPositionId || !selectedPosition) return;
    const name = newSkillName.trim();
    if (!name) return;
    try {
      const newItem = await quickCreateSkill(
        name,
        groupId,
        selectedPositionId,
        selectedPosition.sectorId || '',
        selectedPosition.code
      );
      setPositionDetail((prev) => {
        if (!prev) return prev;
        const updated = { ...prev.skillsByGroup };
        updated[groupId] = [...(updated[groupId] || []), newItem];
        return { ...prev, skillsByGroup: updated };
      });
      setNewSkillName('');
    } catch (err) {
      alert(`Erreur: ${err}`);
    }
  };

  const handleDeleteSkill = async (skillId: string, groupId: string) => {
    try {
      await deleteReferentialItem('skills', skillId);
      setPositionDetail((prev) => {
        if (!prev) return prev;
        const updated = { ...prev.skillsByGroup };
        updated[groupId] = (updated[groupId] || []).filter((s) => s.id !== skillId);
        return { ...prev, skillsByGroup: updated };
      });
    } catch (err) {
      alert(`Erreur: ${err}`);
    }
  };

  const handleDeleteGroup = async (groupId: string) => {
    if (!confirm('Supprimer ce groupe et toutes ses compétences ?')) return;
    try {
      await deleteReferentialItem('skill_groups', groupId);
      setPositionDetail((prev) => {
        if (!prev) return prev;
        const groups = prev.groups.filter((g) => g.id !== groupId);
        const skillsByGroup = { ...prev.skillsByGroup };
        delete skillsByGroup[groupId];
        return { groups, skillsByGroup };
      });
    } catch (err) {
      alert(`Erreur: ${err}`);
    }
  };

  const handleDeletePosition = async (posId: string) => {
    if (!confirm('Supprimer ce poste et tout son contenu ?')) return;
    try {
      await deleteReferentialItem('positions', posId);
      setPositions((prev) => prev.filter((p) => p.id !== posId));
      if (selectedPositionId === posId) {
        setSelectedPositionId(null);
      }
    } catch (err) {
      alert(`Erreur: ${err}`);
    }
  };

  const handleTogglePositionActive = async (posId: string, active: boolean) => {
    try {
      await toggleReferentialActive('positions', posId, !active);
      setPositions((prev) => prev.map((p) => p.id === posId ? { ...p, active: !active } : p));
    } catch (err) {
      alert(`Erreur: ${err}`);
    }
  };

  const handleEditSave = async () => {
    if (!editItem) return;
    const name = editNameFr.trim();
    if (!name) return;
    try {
      const table = editItem.type === 'position' ? 'positions' : editItem.type === 'group' ? 'skill_groups' : 'skills';
      await updateReferentialItem(table, editItem.item.id, { nameFr: name });
      if (editItem.type === 'position') {
        setPositions((prev) => prev.map((p) => p.id === editItem.item.id ? { ...p, nameFr: name } : p));
      } else if (editItem.type === 'group') {
        setPositionDetail((prev) => {
          if (!prev) return prev;
          return { ...prev, groups: prev.groups.map((g) => g.id === editItem.item.id ? { ...g, nameFr: name } : g) };
        });
      } else {
        setPositionDetail((prev) => {
          if (!prev) return prev;
          const updated = { ...prev.skillsByGroup };
          for (const gid of Object.keys(updated)) {
            updated[gid] = updated[gid].map((s) => s.id === editItem.item.id ? { ...s, nameFr: name } : s);
          }
          return { ...prev, skillsByGroup: updated };
        });
      }
      setEditItem(null);
    } catch (err) {
      alert(`Erreur: ${err}`);
    }
  };

  // ─── Translation handler ────────────────────────────────────────────────

  const refreshPositionDetail = useCallback(async () => {
    if (!selectedPositionId) return;
    try {
      const groups = await fetchReferential('skill_groups');
      const positionGroups = groups.filter((g) => g.positionId === selectedPositionId);
      const skillsByGroup: Record<string, RefItem[]> = {};
      await Promise.all(
        positionGroups.map(async (g) => {
          skillsByGroup[g.id] = await fetchSkillsByGroup(g.id);
        })
      );
      setPositionDetail({ groups: positionGroups, skillsByGroup });
      // Also refresh positions list for the selected one
      const updatedPositions = await fetchReferential('positions');
      setPositions(updatedPositions);
    } catch { /* silent */ }
  }, [selectedPositionId]);

  const handleTranslatePosition = async () => {
    if (!selectedPositionId || !positionDetail) return;
    setTranslating(true);
    try {
      const groupIds = positionDetail.groups.map((g) => g.id);
      const skillIds = Object.values(positionDetail.skillsByGroup).flat().map((s) => s.id);

      const results = await Promise.all([
        triggerTranslation('positions', [selectedPositionId]),
        groupIds.length > 0 ? triggerTranslation('skill_groups', groupIds) : Promise.resolve(null),
        skillIds.length > 0 ? triggerTranslation('skills', skillIds) : Promise.resolve(null),
      ]);

      const totalTranslated = results.reduce((acc, r) => acc + (r?.translated || 0), 0);
      const totalItems = 1 + groupIds.length + skillIds.length;

      // Refresh data to update indicators
      await refreshPositionDetail();

      if (totalTranslated > 0) {
        alert(`Traduction terminée : ${totalTranslated}/${totalItems} éléments traduits en 6 langues.`);
      } else if (results.some(r => r === null)) {
        alert(`Erreur lors de la traduction. Vérifiez les logs et la configuration OPENAI_API_KEY.`);
      } else {
        alert(`Aucun élément n'a été traduit (déjà à jour ?).`);
      }
    } catch (err) {
      alert(`Erreur: ${err}`);
    } finally {
      setTranslating(false);
    }
  };

  const openTranslationPanel = (item: RefItem, table: RefTable) => {
    setTranslationItem({ item, table });
    setTranslationEdits({
      en: item.nameEn,
      ar: item.nameAr,
      tr: item.nameTr,
      es: item.nameEs,
      pt: item.namePt,
      vi: item.nameVi,
    });
  };

  const handleSaveTranslation = async (lang: string) => {
    if (!translationItem) return;
    setTranslationSaving(true);
    try {
      await updateTranslation(translationItem.table, translationItem.item.id, lang, translationEdits[lang] || '', true);
      // Update local state
      const updated = { ...translationItem.item, [`name${lang.charAt(0).toUpperCase() + lang.slice(1)}`]: translationEdits[lang] };
      updated.translationLocked = { ...(updated.translationLocked || {}), [lang]: true };
      setTranslationItem({ ...translationItem, item: updated });
      await refreshPositionDetail();
    } catch (err) {
      alert(`Erreur: ${err}`);
    } finally {
      setTranslationSaving(false);
    }
  };

  const handleToggleLock = async (lang: string) => {
    if (!translationItem) return;
    const currentLocked = translationItem.item.translationLocked?.[lang] || false;
    try {
      const newLocks = await toggleTranslationLock(translationItem.table, translationItem.item.id, lang, !currentLocked);
      const updated = { ...translationItem.item, translationLocked: Object.keys(newLocks).length > 0 ? newLocks : null };
      setTranslationItem({ ...translationItem, item: updated });
    } catch (err) {
      alert(`Erreur: ${err}`);
    }
  };

  // ─── Import handlers ───────────────────────────────────────────────────

  const handleFileUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportLog([]);
    setImportPreview(null);
    try {
      let preview: ImportPreview;
      if (file.name.endsWith('.docx')) {
        preview = await parseDocxFile(file);
      } else if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
        preview = await parseExcelFile(file);
      } else {
        alert('Format non supporté. Utilisez .docx ou .xlsx');
        return;
      }
      setImportPreview(preview);
      setImportLog([`Fichier parsé : ${preview.positions.length} postes, ${preview.totalGroups} groupes, ${preview.totalSkills} compétences`]);
    } catch (err) {
      setImportLog([`Erreur de parsing : ${err}`]);
    }
  }, []);

  const handleExecuteImport = async () => {
    if (!importPreview || !importSectorId) return;
    setImporting(true);
    setImportLog((prev) => [...prev, 'Import en cours...']);
    try {
      const result = await executeImport(importPreview, importSectorId, importSubsectorId || undefined);
      setImportLog((prev) => [
        ...prev,
        `Import terminé : ${result.positionsCreated} postes, ${result.groupsCreated} groupes, ${result.skillsCreated} compétences`,
        ...result.errors.map((e) => `Erreur: ${e}`),
      ]);
      const [newPositions] = await Promise.all([fetchReferential('positions')]);
      setPositions(newPositions);
    } catch (err) {
      setImportLog((prev) => [...prev, `Erreur fatale : ${err}`]);
    } finally {
      setImporting(false);
    }
  };

  // ─── Render ────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#00244F]/20 border-t-[#00244F] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-80px)] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white flex-shrink-0">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Référentiels</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {sectors.length} secteurs, {positions.length} postes
          </p>
        </div>
        <button
          onClick={() => setShowImportModal(true)}
          className="px-4 py-2 text-sm font-medium text-white bg-[#00244F] hover:bg-[#001B3D] rounded-lg transition-colors flex items-center gap-2"
        >
          <span>📄</span> Importer un fichier
        </button>
      </div>

      {/* Split panel */}
      <div className="flex flex-1 min-h-0">
        {/* Left panel */}
        <div className="w-[320px] border-r border-gray-200 bg-gray-50/50 flex flex-col flex-shrink-0">
          {/* Search */}
          <div className="p-3 border-b border-gray-200">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher un poste ou une compétence..."
              className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00244F]/20 placeholder-gray-400"
            />
          </div>

          {/* Sectors accordion */}
          <div className="flex-1 overflow-y-auto">
            {sectors.map((sector) => {
              const isExpanded = expandedSectors.has(sector.id);
              const sectorPositions = filteredPositionsBySector(sector.id);

              return (
                <div key={sector.id} className="border-b border-gray-100 last:border-b-0">
                  <button
                    onClick={() => {
                      setExpandedSectors((prev) => {
                        const next = new Set(prev);
                        if (next.has(sector.id)) {
                          next.delete(sector.id);
                        } else {
                          next.add(sector.id);
                          const subs = subsectors.filter((ss) => ss.sectorId === sector.id);
                          setExpandedSubsectors((p) => {
                            const n = new Set(p);
                            subs.forEach((s) => n.add(s.id));
                            return n;
                          });
                        }
                        return next;
                      });
                    }}
                    className="w-full px-4 py-3 flex items-center gap-2 hover:bg-gray-100 transition-colors text-left"
                  >
                    <span className={`text-[10px] text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}>
                      ▶
                    </span>
                    <span className="text-sm font-semibold text-gray-800 flex-1">{sector.nameFr}</span>
                    <span className="px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-gray-200 text-gray-500">
                      {sectorPositions.length}
                    </span>
                  </button>

                  {isExpanded && (
                    <div className="pb-2">
                      {/* Positions grouped by category > subsector (foldable) */}
                      {getGroupedSubsectors(sector.id).map((catGroup, cgIdx) => {
                        const visibleSubs = catGroup.subs.filter(
                          (ss) => !(filteredPositionsBySubsector(ss.id).length === 0 && searchQuery)
                        );
                        if (visibleSubs.length === 0) return null;
                        return (
                          <div key={catGroup.category || `cat_${cgIdx}`}>
                            {catGroup.category && (
                              <div className="px-4 pl-5 pt-2 pb-0.5 text-[10px] uppercase tracking-widest text-[#00244F]/60 font-bold">
                                {catGroup.category}
                              </div>
                            )}
                            {visibleSubs.map((ss) => {
                        const ssPositions = filteredPositionsBySubsector(ss.id);
                        const isSubExpanded = expandedSubsectors.has(ss.id);
                        return (
                          <div key={ss.id}>
                            <button
                              onClick={() => {
                                setExpandedSubsectors((prev) => {
                                  const next = new Set(prev);
                                  if (next.has(ss.id)) next.delete(ss.id);
                                  else next.add(ss.id);
                                  return next;
                                });
                              }}
                              className="w-full px-4 pl-6 py-1.5 flex items-center gap-1.5 hover:bg-gray-100 transition-colors mt-1 text-left"
                            >
                              <span className={`text-[8px] text-gray-400 transition-transform duration-200 ${isSubExpanded ? 'rotate-90' : ''}`}>
                                ▶
                              </span>
                              <span className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold flex-1">
                                {ss.nameFr}
                              </span>
                              <span className="text-[10px] text-gray-300">{ssPositions.length}</span>
                            </button>
                            {isSubExpanded && (
                              <>
                                {/* Insert point at top */}
                                <InsertPoint
                                  sectorId={sector.id}
                                  subsectorId={ss.id}
                                  afterId="top"
                                  addingPositionInSector={addingPositionInSector}
                                  addingPositionAfter={addingPositionAfter}
                                  newPositionName={newPositionName}
                                  setNewPositionName={setNewPositionName}
                                  onInsert={() => handleAddPosition(sector.id, ss.id)}
                                  onCancel={() => { setAddingPositionInSector(null); setAddingPositionAfter(null); setNewPositionName(''); }}
                                  onActivate={() => { setAddingPositionInSector(sector.id); setAddingPositionAfter('top'); setNewPositionName(''); }}
                                  indent="pl-10"
                                />
                                {ssPositions.map((pos) => (
                                  <React.Fragment key={pos.id}>
                                    <button
                                      onClick={() => setSelectedPositionId(pos.id)}
                                      className={`w-full px-4 pl-10 py-2 text-left text-sm transition-colors flex items-center gap-2 ${
                                        selectedPositionId === pos.id
                                          ? 'bg-[#00244F]/10 text-[#00244F] font-medium border-r-2 border-[#00244F]'
                                          : 'text-gray-700 hover:bg-gray-100'
                                      }`}
                                    >
                                      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${pos.active ? 'bg-green-400' : 'bg-gray-300'}`} />
                                      <span className="truncate">{pos.nameFr}</span>
                                    </button>
                                    <InsertPoint
                                      sectorId={sector.id}
                                      subsectorId={ss.id}
                                      afterId={pos.id}
                                      addingPositionInSector={addingPositionInSector}
                                      addingPositionAfter={addingPositionAfter}
                                      newPositionName={newPositionName}
                                      setNewPositionName={setNewPositionName}
                                      onInsert={() => handleAddPosition(sector.id, ss.id)}
                                      onCancel={() => { setAddingPositionInSector(null); setAddingPositionAfter(null); setNewPositionName(''); }}
                                      onActivate={() => { setAddingPositionInSector(sector.id); setAddingPositionAfter(pos.id); setNewPositionName(''); }}
                                      indent="pl-10"
                                    />
                                  </React.Fragment>
                                ))}
                              </>
                            )}
                          </div>
                        );
                            })}
                          </div>
                        );
                      })}

                      {/* Positions without subsector */}
                      {unclassifiedPositions(sector.id).length > 0 && (
                        <div>
                          {getSubsectorsForSector(sector.id).length > 0 && (
                            <div className="px-4 pl-6 py-1.5 text-[10px] uppercase tracking-wider text-gray-400 font-semibold mt-1">
                              Autres
                            </div>
                          )}
                          {(() => {
                            const hasSubs = getSubsectorsForSector(sector.id).length > 0;
                            const indent = hasSubs ? 'pl-10' : 'pl-8';
                            const ucPositions = unclassifiedPositions(sector.id);
                            return (
                              <>
                                <InsertPoint
                                  sectorId={sector.id}
                                  afterId="top"
                                  addingPositionInSector={addingPositionInSector}
                                  addingPositionAfter={addingPositionAfter}
                                  newPositionName={newPositionName}
                                  setNewPositionName={setNewPositionName}
                                  onInsert={() => handleAddPosition(sector.id)}
                                  onCancel={() => { setAddingPositionInSector(null); setAddingPositionAfter(null); setNewPositionName(''); }}
                                  onActivate={() => { setAddingPositionInSector(sector.id); setAddingPositionAfter('top'); setNewPositionName(''); }}
                                  indent={indent}
                                />
                                {ucPositions.map((pos) => (
                                  <React.Fragment key={pos.id}>
                                    <button
                                      onClick={() => setSelectedPositionId(pos.id)}
                                      className={`w-full px-4 ${indent} py-2 text-left text-sm transition-colors flex items-center gap-2 ${
                                        selectedPositionId === pos.id
                                          ? 'bg-[#00244F]/10 text-[#00244F] font-medium border-r-2 border-[#00244F]'
                                          : 'text-gray-700 hover:bg-gray-100'
                                      }`}
                                    >
                                      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${pos.active ? 'bg-green-400' : 'bg-gray-300'}`} />
                                      <span className="truncate">{pos.nameFr}</span>
                                    </button>
                                    <InsertPoint
                                      sectorId={sector.id}
                                      afterId={pos.id}
                                      addingPositionInSector={addingPositionInSector}
                                      addingPositionAfter={addingPositionAfter}
                                      newPositionName={newPositionName}
                                      setNewPositionName={setNewPositionName}
                                      onInsert={() => handleAddPosition(sector.id)}
                                      onCancel={() => { setAddingPositionInSector(null); setAddingPositionAfter(null); setNewPositionName(''); }}
                                      onActivate={() => { setAddingPositionInSector(sector.id); setAddingPositionAfter(pos.id); setNewPositionName(''); }}
                                      indent={indent}
                                    />
                                  </React.Fragment>
                                ))}
                              </>
                            );
                          })()}
                        </div>
                      )}

                      {/* Fallback add button when no positions at all */}
                      {unclassifiedPositions(sector.id).length === 0 && getSubsectorsForSector(sector.id).length === 0 && (
                        <>
                          {addingPositionInSector === sector.id && !addingPositionAfter ? (
                            <div className="px-4 pl-8 py-1.5">
                              <input
                                type="text"
                                value={newPositionName}
                                onChange={(e) => setNewPositionName(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleAddPosition(sector.id);
                                  if (e.key === 'Escape') { setAddingPositionInSector(null); setNewPositionName(''); }
                                }}
                                onBlur={() => { if (!newPositionName.trim()) { setAddingPositionInSector(null); } }}
                                placeholder="Nom du poste..."
                                autoFocus
                                className="w-full px-2 py-1.5 text-sm border border-[#00244F]/30 rounded-md focus:outline-none focus:ring-2 focus:ring-[#00244F]/20 bg-white"
                              />
                            </div>
                          ) : (
                            <button
                              onClick={() => { setAddingPositionInSector(sector.id); setAddingPositionAfter(null); setNewPositionName(''); }}
                              className="w-full px-4 pl-8 py-1.5 text-left text-xs text-gray-400 hover:text-[#00244F] hover:bg-gray-100 transition-colors"
                            >
                              + Ajouter un poste
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Right panel */}
        <div className="flex-1 overflow-y-auto bg-white">
          {!selectedPositionId ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="text-5xl mb-4 opacity-30">⚙️</div>
                <p className="text-gray-400 text-sm">Sélectionnez un poste pour voir ses compétences</p>
              </div>
            </div>
          ) : detailLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="w-6 h-6 border-2 border-[#00244F]/20 border-t-[#00244F] rounded-full animate-spin" />
            </div>
          ) : selectedPosition && positionDetail ? (
            <div className="p-6">
              {/* Position header */}
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                    <span
                      className="cursor-pointer hover:text-[#00244F]"
                      onClick={() => openTranslationPanel(selectedPosition, 'positions')}
                      title="Voir les traductions"
                    >
                      {selectedPosition.nameFr}
                    </span>
                    {!selectedPosition.translated ? (
                      <span className="px-2 py-0.5 text-[10px] bg-orange-100 text-orange-600 font-medium rounded-md">
                        Non traduit
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 text-[10px] bg-green-100 text-green-600 font-medium rounded-md">
                        Traduit ✓
                      </span>
                    )}
                  </h2>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className="px-2 py-0.5 text-[10px] bg-blue-50 text-blue-600 font-medium rounded-md">
                      {getSectorName(selectedPosition.sectorId || '')}
                    </span>
                    {selectedPosition.category && (
                      <span className="px-2 py-0.5 text-[10px] bg-indigo-50 text-indigo-600 font-medium rounded-md">
                        {selectedPosition.category}
                      </span>
                    )}
                    <span className="px-2 py-0.5 text-[10px] bg-gray-100 text-gray-500 font-mono rounded">
                      {selectedPosition.code}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleTogglePositionActive(selectedPosition.id, selectedPosition.active)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                      selectedPosition.active
                        ? 'bg-green-50 text-green-700 hover:bg-green-100'
                        : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                    }`}
                  >
                    {selectedPosition.active ? 'Actif' : 'Inactif'}
                  </button>
                  <button
                    onClick={handleTranslatePosition}
                    disabled={translating}
                    className="px-3 py-1.5 text-xs font-medium text-purple-600 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors disabled:opacity-40 flex items-center gap-1"
                  >
                    {translating ? (
                      <><span className="w-3 h-3 border border-purple-400 border-t-transparent rounded-full animate-spin" /> Traduction...</>
                    ) : (
                      'Traduire'
                    )}
                  </button>
                  <button
                    onClick={() => { setEditItem({ item: selectedPosition, type: 'position' }); setEditNameFr(selectedPosition.nameFr); }}
                    className="px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                  >
                    Renommer
                  </button>
                  <button
                    onClick={() => handleDeletePosition(selectedPosition.id)}
                    className="px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
                  >
                    Supprimer
                  </button>
                </div>
              </div>

              {/* Groups & Skills */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-700">
                    Groupes de compétences ({positionDetail.groups.length})
                  </h3>
                </div>

                {positionDetail.groups.map((group) => {
                  const isGroupExpanded = expandedGroups.has(group.id);
                  const skills = positionDetail.skillsByGroup[group.id] || [];

                  return (
                    <div key={group.id} className="border border-gray-200 rounded-xl overflow-hidden">
                      {/* Group header */}
                      <div
                        className="px-4 py-3 bg-gray-50 flex items-center gap-2 cursor-pointer hover:bg-gray-100 transition-colors"
                        onClick={() => {
                          setExpandedGroups((prev) => {
                            const next = new Set(prev);
                            if (next.has(group.id)) next.delete(group.id);
                            else next.add(group.id);
                            return next;
                          });
                        }}
                      >
                        <span className={`text-[10px] text-gray-400 transition-transform duration-200 ${isGroupExpanded ? 'rotate-90' : ''}`}>
                          ▶
                        </span>
                        <span
                          className="text-sm font-medium text-gray-900 flex-1 cursor-pointer hover:text-[#00244F]"
                          onClick={(e) => { e.stopPropagation(); openTranslationPanel(group, 'skill_groups'); }}
                        >{group.nameFr}</span>
                        <span className="px-2 py-0.5 text-[10px] bg-white border border-gray-200 text-gray-500 rounded-full font-medium">
                          {skills.length} comp.
                        </span>
                        <button
                          onClick={(e) => { e.stopPropagation(); setEditItem({ item: group, type: 'group' }); setEditNameFr(group.nameFr); }}
                          className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                          title="Renommer"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDeleteGroup(group.id); }}
                          className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                          title="Supprimer"
                        >
                          🗑️
                        </button>
                      </div>

                      {/* Skills chips */}
                      {isGroupExpanded && (
                        <div className="px-4 py-3">
                          <div className="flex flex-wrap gap-2 mb-3">
                            {skills.length === 0 && (
                              <p className="text-xs text-gray-400 italic">Aucune compétence dans ce groupe</p>
                            )}
                            {skills.map((skill) => (
                              <span
                                key={skill.id}
                                className="group inline-flex items-center gap-1 px-2.5 py-1.5 text-xs bg-white border border-gray-200 rounded-lg text-gray-700 hover:border-gray-300 transition-colors"
                              >
                                {!skill.translated ? (
                                  <span className="w-1.5 h-1.5 rounded-full bg-orange-400 flex-shrink-0" title="Traduction en attente" />
                                ) : (
                                  <span className="w-1.5 h-1.5 rounded-full bg-green-400 flex-shrink-0" title="Traduit" />
                                )}
                                <span
                                  className="cursor-pointer hover:text-[#00244F]"
                                  onClick={() => openTranslationPanel(skill, 'skills')}
                                >
                                  {skill.nameFr}
                                </span>
                                <button
                                  onClick={() => handleDeleteSkill(skill.id, group.id)}
                                  className="ml-0.5 text-gray-300 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
                                >
                                  ×
                                </button>
                              </span>
                            ))}
                          </div>

                          {/* Inline add skill */}
                          {addingSkillInGroup === group.id ? (
                            <input
                              ref={skillInputRef}
                              type="text"
                              value={newSkillName}
                              onChange={(e) => setNewSkillName(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  handleAddSkill(group.id);
                                }
                                if (e.key === 'Escape') {
                                  setAddingSkillInGroup(null);
                                  setNewSkillName('');
                                }
                              }}
                              placeholder="Nom de la compétence + Entrée"
                              autoFocus
                              className="w-full px-3 py-2 text-sm border border-[#00244F]/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00244F]/20 bg-gray-50"
                            />
                          ) : (
                            <button
                              onClick={() => { setAddingSkillInGroup(group.id); setNewSkillName(''); }}
                              className="w-full px-3 py-2 text-left text-xs text-gray-400 hover:text-[#00244F] border border-dashed border-gray-200 hover:border-[#00244F]/30 rounded-lg transition-colors"
                            >
                              + Ajouter une compétence
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Inline add group */}
                {addingGroup ? (
                  <div className="border border-dashed border-[#00244F]/30 rounded-xl p-3">
                    <input
                      ref={groupInputRef}
                      type="text"
                      value={newGroupName}
                      onChange={(e) => setNewGroupName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleAddGroup();
                        if (e.key === 'Escape') { setAddingGroup(false); setNewGroupName(''); }
                      }}
                      onBlur={() => { if (!newGroupName.trim()) setAddingGroup(false); }}
                      placeholder="Nom du groupe + Entrée"
                      autoFocus
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00244F]/20"
                    />
                  </div>
                ) : (
                  <button
                    onClick={() => { setAddingGroup(true); setNewGroupName(''); }}
                    className="w-full px-4 py-3 text-sm text-gray-500 hover:text-[#00244F] border border-dashed border-gray-200 hover:border-[#00244F]/30 rounded-xl transition-colors font-medium"
                  >
                    + Ajouter un groupe de compétences
                  </button>
                )}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {/* Edit modal */}
      {editItem && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setEditItem(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">
                Renommer {editItem.type === 'position' ? 'le poste' : editItem.type === 'group' ? 'le groupe' : 'la compétence'}
              </h3>
            </div>
            <div className="p-6">
              <input
                type="text"
                value={editNameFr}
                onChange={(e) => setEditNameFr(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleEditSave(); }}
                autoFocus
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00244F]/20"
              />
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-3 bg-gray-50">
              <button onClick={() => setEditItem(null)} className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors">
                Annuler
              </button>
              <button onClick={handleEditSave} className="px-4 py-2 text-sm font-medium text-white bg-[#00244F] hover:bg-[#001B3D] rounded-lg transition-colors">
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Translation detail modal */}
      {translationItem && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setTranslationItem(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
              <h3 className="text-lg font-semibold text-gray-900">Traductions</h3>
              <p className="text-sm text-gray-500 mt-0.5">
                🇫🇷 {translationItem.item.nameFr}
              </p>
            </div>
            <div className="p-6 space-y-3">
              {TRANSLATION_LANGS.map(({ key, label }) => {
                const isLocked = translationItem.item.translationLocked?.[key] || false;
                const currentValue = translationEdits[key] || '';
                return (
                  <div key={key} className="flex items-center gap-2">
                    <div className="flex-1">
                      <label className="block text-[10px] font-medium text-gray-500 uppercase mb-0.5">
                        {label} ({key})
                      </label>
                      <input
                        type="text"
                        value={currentValue}
                        onChange={(e) => setTranslationEdits((prev) => ({ ...prev, [key]: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleSaveTranslation(key); }}
                        className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00244F]/20 ${
                          currentValue ? 'border-gray-200 bg-white' : 'border-orange-200 bg-orange-50'
                        }`}
                        placeholder={`Traduction ${label}...`}
                      />
                    </div>
                    <div className="flex flex-col items-center gap-1 pt-4">
                      <button
                        onClick={() => handleSaveTranslation(key)}
                        disabled={translationSaving}
                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-40"
                        title="Sauvegarder + verrouiller"
                      >
                        💾
                      </button>
                      <button
                        onClick={() => handleToggleLock(key)}
                        className={`p-1.5 rounded-lg transition-colors ${
                          isLocked ? 'text-amber-600 bg-amber-50 hover:bg-amber-100' : 'text-gray-400 hover:bg-gray-100'
                        }`}
                        title={isLocked ? 'Déverrouiller (la prochaine traduction auto écrasera)' : 'Verrouiller (protège des batch)'}
                      >
                        {isLocked ? '🔒' : '🔓'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between bg-gray-50 sticky bottom-0">
              <p className="text-[10px] text-gray-400">
                🔒 = protégé des traductions batch automatiques
              </p>
              <button onClick={() => setTranslationItem(null)} className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors">
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setShowImportModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 overflow-hidden max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white z-10">
              <h3 className="text-lg font-semibold text-gray-900">Importer un fichier</h3>
              <button onClick={() => setShowImportModal(false)} className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-600">
                ✕
              </button>
            </div>
            <div className="p-6 space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Secteur cible</label>
                  <select
                    value={importSectorId}
                    onChange={(e) => { setImportSectorId(e.target.value); setImportSubsectorId(''); }}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00244F]/20"
                  >
                    <option value="">Choisir un secteur</option>
                    {sectors.map((s) => (
                      <option key={s.id} value={s.id}>{s.nameFr}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sous-secteur (optionnel)</label>
                  <select
                    value={importSubsectorId}
                    onChange={(e) => setImportSubsectorId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00244F]/20"
                  >
                    <option value="">Aucun</option>
                    {subsectors
                      .filter((ss) => ss.sectorId === importSectorId)
                      .map((ss) => (
                        <option key={ss.id} value={ss.id}>
                          {ss.category ? `${ss.category} – ${ss.nameFr}` : ss.nameFr}
                        </option>
                      ))}
                  </select>
                </div>
              </div>

              <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-[#00244F]/40 transition-colors">
                <input type="file" accept=".docx,.xlsx,.xls" onChange={handleFileUpload} className="hidden" id="import-file" />
                <label htmlFor="import-file" className="cursor-pointer">
                  <div className="text-4xl mb-3">📄</div>
                  <p className="text-sm font-medium text-gray-700">Cliquer pour importer un fichier</p>
                  <p className="text-xs text-gray-400 mt-1">Formats acceptés : .docx, .xlsx</p>
                </label>
              </div>

              {importPreview && (
                <div className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-blue-800 mb-2">Aperçu</h4>
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <div className="text-2xl font-bold text-blue-700">{importPreview.positions.length}</div>
                        <div className="text-xs text-blue-600">Postes</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-blue-700">{importPreview.totalGroups}</div>
                        <div className="text-xs text-blue-600">Groupes</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-blue-700">{importPreview.totalSkills}</div>
                        <div className="text-xs text-blue-600">Compétences</div>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={handleExecuteImport}
                    disabled={!importSectorId || importing}
                    className="w-full px-4 py-3 text-sm font-medium text-white bg-[#00244F] hover:bg-[#001B3D] rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {importing ? 'Import en cours...' : `Importer ${importPreview.positions.length} postes`}
                  </button>
                </div>
              )}

              {importLog.length > 0 && (
                <div className="bg-gray-900 rounded-lg p-4 max-h-32 overflow-y-auto">
                  {importLog.map((line, i) => (
                    <div key={i} className={`text-xs font-mono ${line.startsWith('Erreur') ? 'text-red-400' : 'text-green-400'}`}>
                      {line}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Referentials;
